import { supabase } from '../../infrastructure/supabase/supabaseClient';

export class InvitationService {
  /**
   * Generates a new 8-character invitation code for a clinical professional.
   * Only active verified professionals can call this function.
   * Throws an error if the host is not a clinical professional or has reached the active limit.
   */
  static async generateInvitationCode(): Promise<string> {
    const { data, error } = await supabase.rpc('fn_generar_invitacion_profesional');
    
    if (error) {
      console.error('Error in generateInvitationCode:', error);
      throw new Error(error.message || 'Error al generar la invitación');
    }
    
    return data;
  }

  /**
   * Accepts an invitation code and links the user in the Tree of Trust.
   * Instantly grants the PROFESIONAL_CLINICO role.
   * Throws an error if the code is invalid or expired.
   */
  static async acceptInvitation(code: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('fn_aceptar_invitacion_profesional', { p_codigo: code });
    
    if (error) {
      console.error('Error in acceptInvitation:', error);
      throw new Error(error.message || 'Código de invitación inválido o expirado');
    }
    
    return data;
  }

  /**
   * Obtiene la cantidad de invitaciones restantes que el usuario puede generar.
   * El límite simultáneo es 3.
   */
  static async getRemainingInvitations(): Promise<number> {
    const { count, error } = await supabase
      .from('invitaciones_profesionales')
      .select('*', { count: 'exact', head: true })
      .eq('usada', false)
      .gt('expires_at', new Date().toISOString());

    if (error) {
      console.error('Error fetching invitations count:', error);
      return 0; // Ante la duda, limitamos para evitar spam de errores
    }

    const activeCount = count || 0;
    return Math.max(0, 3 - activeCount);
  }

  /**
   * Obtiene la lista detallada de invitaciones que el usuario ha generado
   * y que aún están pendientes (sin usar y vigentes).
   */
  static async getActiveInvitations(): Promise<{id: string, code: string, expires_at: string}[]> {
    const { data, error } = await supabase
      .from('invitaciones_profesionales')
      .select('invitacion_id, codigo_invitacion, expires_at')
      .eq('usada', false)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching active invitations:', error);
      return [];
    }

    return data.map(item => ({
      id: item.invitacion_id,
      code: item.codigo_invitacion,
      expires_at: item.expires_at
    }));
  }
}
